import { useState } from "react"

import { cloneDeep } from "lodash"
import { XIcon, PlusIcon, Trash, ImageIcon } from "lucide-react"
import { Input, DefaultPopover, Separator, InputGroup, InputGroupText, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Button } from "twyne-new.ui"

import ImageField from '@/components/custom_fields/fields/ImageField'

import { hasKey } from "../../utils"
import GradientPicker from "../components/gradient/Picker"
import GradientPreview from "../components/gradient/Preview"

export default function General({ properties = {}, onChange }) {
  const handleRemove = key => {
    const copiedProperties = cloneDeep(properties)
    delete copiedProperties[key]
    onChange({ properties: copiedProperties })
  }

  return <div className='mb-2 flex flex-col gap-2'>
    <div className='flex justify-end w-full'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size='sm' disabled={hasKey(properties, 'image') && hasKey(properties, 'gradient')} variant='light'>
            <PlusIcon size='14' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {!hasKey(properties, 'image') && <>
            <DropdownMenuItem onClick={() => onChange({ properties: { ...properties, image: {} } })}>
              Image
            </DropdownMenuItem>
          </>}
          {!hasKey(properties, 'gradient') && <>
            <DropdownMenuItem onClick={() => onChange({ properties: { ...properties, gradient: {} } })}>
              Gradient
            </DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    {properties.gradient && <GradientItem
      gradient={properties.gradient}
      onRemove={() => handleRemove('gradient')}
      onChange={value => onChange({ properties: { ...properties, gradient: value } })}
    />}

    {properties.image && <ImageItem
      image={properties.image}
      onRemove={() => handleRemove('image')}
      onChange={value => onChange({ properties: { ...properties, image: value } })}
    />}
  </div>
}

const ImageItem = ({ image, onRemove, onChange }) => {
  const [open, setOpen] = useState(false)

  return <DefaultPopover
    trigger={<div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <ImagePreview image={image} />
      </div>
      <Button variant='light' size='sm' onClick={() => onRemove('gradient')}>
        <Trash size='14' />
      </Button>
    </div>}
    triggerProps={{ asChild: true }}
    contentProps={{ side: 'left', align: 'center' }}
    wrapperProps={{ open: open, onOpenChange: setOpen }}
  >
    <div className='flex justify-between items-center'>
      Image
      <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
    </div>
    <Separator className='m-0 my-2' />
    <ImageField value={image} onChange={({ value: newValue }) => onChange({ ...newValue })} />
  </DefaultPopover>
}

const ImagePreview = ({ image }) => <InputGroup>
  <InputGroupText addonType='prepend'>
    {!image?.src ? <ImageIcon size={14} /> : <img src={image.src} alt="feature_image" className='w-4 h-4 cursor-pointer' />}
  </InputGroupText>
  <Input className='text-left' value={image?.name} readOnly placeholder='Image' />
</InputGroup>

const GradientItem = ({ gradient, onRemove, onChange }) => {
  const [open, setOpen] = useState(false)

  return <DefaultPopover
    trigger={<div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <GradientPreview gradient={gradient} />
      </div>
      <Button variant='light' size='sm' onClick={onRemove}>
        <Trash size='14' />
      </Button>
    </div>}
    triggerProps={{ asChild: true }}
    contentProps={{ className: 'w-96', side: 'left', align: 'center' }}
    wrapperProps={{ open: open, onOpenChange: setOpen }}
  >
    <div className='flex justify-between items-center'>
      Gradient
      <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
    </div>
    <Separator className='m-0 my-2' />
    <GradientPicker value={gradient} onChange={onChange} />
  </DefaultPopover>
}
